import { ApiErrorStatusCode } from 'lib-shared-api-utils';
import { useCallback, useMemo, useState } from 'react';
import { customFieldsApi } from 'apis/customFields';
import { getAsyncStatus, getAsyncStatusFromRtkQueryStates, isQueryError, } from 'utils/apis/axios';
import { getCustomFields } from '../getCustomFields';
/**
 * A hook for lazily getting custom fields (attributes and their values) of an
 * instance of an entity.
 */
export const useLazyCustomFields = () => {
    var _a, _b;
    const [readAttributes, attributesResponse] = customFieldsApi.useLazyReadAttributesQuery();
    const [readValue, valueResponse] = customFieldsApi.useLazyReadValueQuery();
    const [includeArchived, setIncludeArchived] = useState(false);
    const [includeDeactivated, setIncludeDeactivated] = useState(false);
    const rawResponse = useMemo(() => ({
        attributes: attributesResponse,
        value: valueResponse,
    }), [attributesResponse, valueResponse]);
    const customFields = useMemo(() => {
        var _a, _b;
        return getCustomFields((_a = attributesResponse.data) === null || _a === void 0 ? void 0 : _a.data.attributes, (_b = valueResponse.data) === null || _b === void 0 ? void 0 : _b.data.attribute_values, { includeArchived, includeDeactivated });
    }, [
        (_a = attributesResponse.data) === null || _a === void 0 ? void 0 : _a.data.attributes,
        includeArchived,
        includeDeactivated,
        (_b = valueResponse.data) === null || _b === void 0 ? void 0 : _b.data.attribute_values,
    ]);
    const readAttributesAsyncStatus = getAsyncStatusFromRtkQueryStates(attributesResponse);
    const readValuesAsyncStatus = getAsyncStatusFromRtkQueryStates(valueResponse);
    const isReadValueNotFound = isQueryError(valueResponse.error) &&
        valueResponse.error.status === ApiErrorStatusCode.NotFound;
    const asyncStatus = isReadValueNotFound
        ? readAttributesAsyncStatus
        : getAsyncStatus([readAttributesAsyncStatus, readValuesAsyncStatus]);
    const trigger = useCallback(({ entityId, entityInstanceId, includeArchived = false, includeDeactivated = false, }) => {
        readAttributes({ entityId });
        readValue({ entityId, entityInstanceId });
        setIncludeArchived(includeArchived);
        setIncludeDeactivated(includeDeactivated);
    }, [readAttributes, readValue]);
    const result = useMemo(() => ({
        customFields,
        asyncStatus,
        rawResponse,
    }), [asyncStatus, customFields, rawResponse]);
    return useMemo(() => [trigger, result], [result, trigger]);
};
